import type {
  MentorProfileChangeRequest,
  MentorProfileDraft,
  MentorProfileReviewStatus,
} from "@/lib/api/mentor-profile";
import { flaggedSectionsAddressed } from "@/lib/mentor-profile/revision-change-detection";
import { PROFILE_SECTIONS } from "@/lib/mentor-profile/profile-sections";

const PENDING_REVIEW: MentorProfileReviewStatus[] = [
  "submitted",
  "in_review",
];

const EDITABLE_KEYS = [
  "display_name",
  "languages",
  "bio",
  "favorite_music",
  "profile_picture_url",
  "match_card_photo_url",
  "profile_audio_url",
  "interests",
  "interest_images",
  "vibe_images",
] as const satisfies ReadonlyArray<keyof MentorProfileDraft>;

function normalizeEditableValue(value: unknown): unknown {
  if (value == null) return null;
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === "string")) {
      return [...value].map((item) => item.trim()).sort();
    }
    return value.map((item) =>
      item && typeof item === "object"
        ? Object.fromEntries(
            Object.entries(item as Record<string, unknown>).sort(([a], [b]) =>
              a.localeCompare(b),
            ),
          )
        : item,
    );
  }
  if (typeof value === "string") return value.trim();
  return value;
}

export function draftFingerprint(draft: MentorProfileDraft) {
  const pick: Record<string, unknown> = {};
  for (const key of EDITABLE_KEYS) {
    pick[key] = normalizeEditableValue(draft[key] ?? null);
  }
  return JSON.stringify(pick);
}

export function draftsDiffer(a: MentorProfileDraft, b: MentorProfileDraft) {
  return draftFingerprint(a) !== draftFingerprint(b);
}

export function openChangeRequests(
  requests: MentorProfileChangeRequest[] | null | undefined,
) {
  return (requests ?? []).filter((request) => request.status === "open");
}

/**
 * Single submit/resubmit gate for onboarding review + Settings Profile.
 * Pending review: locked (autosave is already what reviewers see).
 * changes_requested: every flagged section must change vs baseline.
 * Otherwise: draft must differ from published snapshot (or any draft when unpublished).
 */
export function canSubmitProfile({
  status,
  draft,
  publishedSnapshot,
  baseline,
  changeRequests,
  reviewUpdatedAt,
}: {
  status: MentorProfileReviewStatus;
  draft: MentorProfileDraft;
  publishedSnapshot: MentorProfileDraft | null;
  baseline?: MentorProfileDraft | null;
  changeRequests?: MentorProfileChangeRequest[];
  /** ISO timestamp — used as a refresh fallback when baseline was lost. */
  reviewUpdatedAt?: string | null;
}): boolean {
  if (PENDING_REVIEW.includes(status)) return false;

  const open = openChangeRequests(changeRequests);
  if (status === "changes_requested" || open.length > 0) {
    if (baseline) {
      return flaggedSectionsAddressed(draft, baseline, open);
    }
    // No frozen baseline (storage unavailable / first paint). Only then use
    // updated_at as a weak signal that the mentor already patched this round.
    const roundStarted = open[0]?.created_at;
    if (reviewUpdatedAt && roundStarted) {
      const updated = Date.parse(reviewUpdatedAt);
      const started = Date.parse(roundStarted);
      if (
        Number.isFinite(updated) &&
        Number.isFinite(started) &&
        updated > started + 1000
      ) {
        return true;
      }
    }
    return false;
  }

  if (publishedSnapshot) {
    return draftsDiffer(draft, publishedSnapshot);
  }

  // First-time onboarding completeness is enforced by the API on submit.
  return PROFILE_SECTIONS.every((section) => !section.summary(draft).empty);
}
